import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Alert, Box, DialogTitle, IconButton, Typography } from '@mui/material';
import PropTypes from 'prop-types';

import SYSTEM_DEFICIT from 'common/constants/inventoryLabels';
import {
  formatQty,
  isLotUsable,
  shortfallAgainstFill,
  toDateOnly,
} from '../utils/bulkLotNdcStatus';
import { warningTextTone, warningTint, warningTone } from '../utils/warningTone';

/**
 * A claim already committed for this lot: hand-recorded, or confirmed short. The grid reads it as
 * settled (classifyRow), so the dialog must not tell the operator to replace it. Same bounds as the
 * classifier: still in inventory, and not past expiry.
 */
const isSettledClaimLot = ({ lot, todayStr }) => {
  if (lot.existsInInventory === false) return false;

  const expiresOn = toDateOnly(lot.expirationDate);
  if (lot.isManualEntry === true) return !expiresOn || expiresOn >= todayStr;

  return (
    lot.allowShortfallAsUnverified === true &&
    Number(lot.remainingQty) > 0 &&
    !!expiresOn &&
    expiresOn >= todayStr
  );
};

/** The first recorded lot the operator has to replace, or null when every one prints as it is. */
export const findLotNeedingReplacement = ({
  recordedLots,
  fillQuantity,
  todayStr,
  shortfallAllowed,
}) =>
  (recordedLots ?? []).find(
    (lot) =>
      !isLotUsable({ lot, fillQuantity, todayStr }) &&
      !(shortfallAllowed && isSettledClaimLot({ lot, todayStr }))
  ) ?? null;

/**
 * Why the lot on record cannot be printed as it stands, in the operator's words.
 *
 * POM-6119 — a lot short of the fill gets its own sentence. It is on the shelf and in date, and
 * the list underneath offers it, so calling it unusable made the heading contradict the control
 * directly below it. Gated on the same flag the lot list is: with the shortfall path off that lot
 * is filtered out of the picker, so calling it merely short would soften the notice.
 */
export const describeRecordedLotProblem = ({
  lot,
  fillQuantity,
  todayStr,
  shortfallAllowed,
  batchFreeQty = null,
}) => {
  if (!lot) return null;

  // POM-6536 — on a lot other rows of the batch share, say what they leave, as the lot list does.
  const isShared = batchFreeQty != null && Number(batchFreeQty) !== Number(lot.remainingQty);
  const shortBy = isShared
    ? Math.max(0, Number(fillQuantity || 0) - Number(batchFreeQty))
    : shortfallAgainstFill({ lot, fillQuantity });
  const remaining = formatQty(isShared ? batchFreeQty : lot.remainingQty);
  const needed = formatQty(fillQuantity);
  const holds = isShared
    ? `has ${remaining} of ${needed} free in this batch`
    : `holds ${remaining} of ${needed}`;
  const pickAnother = 'Pick another lot to print this item.';

  if (lot.existsInInventory === false) {
    return {
      isShort: false,
      text: `Reserved lot ${lot.lotNumber} is no longer in inventory. ${pickAnother}`,
    };
  }

  const expiresOn = toDateOnly(lot.expirationDate);
  if (!expiresOn) {
    return {
      isShort: false,
      text: `Reserved lot ${lot.lotNumber} has no expiry date on record. ${pickAnother}`,
    };
  }
  if (expiresOn < todayStr) {
    return {
      isShort: false,
      text: `Reserved lot ${lot.lotNumber} expired on ${expiresOn}. ${pickAnother}`,
    };
  }

  if (shortfallAllowed && Number(lot.remainingQty) > 0 && shortBy > 0) {
    return {
      isShort: true,
      text:
        `Reserved lot ${lot.lotNumber} ${holds}. Pick it to dispense ` +
        `${remaining} and claim ${formatQty(shortBy)} as ${SYSTEM_DEFICIT.toLowerCase()}, ` +
        'or pick another lot.',
    };
  }

  return {
    isShort: false,
    text:
      Number(lot.remainingQty) > 0
        ? `Reserved lot ${lot.lotNumber} ${holds}, not enough for this fill. ${pickAnother}`
        : `Reserved lot ${lot.lotNumber} has no stock left. ${pickAnother}`,
  };
};

/**
 * The picker's heading: what is being dispensed, which row it is, and what is wrong with the lot
 * on record. Its own file because ChangeNdcLotDialog sits near the 450-line cap.
 */
const ChangeNdcLotDialogTitle = ({
  item,
  fillQuantity,
  unusableRecordedLot,
  batchFreeQty,
  shortfallAllowed,
  todayStr,
  onClose,
}) => {
  const problem = describeRecordedLotProblem({
    lot: unusableRecordedLot,
    fillQuantity,
    todayStr,
    shortfallAllowed,
    batchFreeQty,
  });

  const rowDetails = [
    item.rxNumber && `Rx ${item.rxNumber}`,
    item.orderId && `Order ${item.orderId}`,
    item.fulfillingPharmacyName,
    `Qty ${formatQty(item.fillQuantity)}`,
  ].filter(Boolean);

  return (
    <DialogTitle sx={{ pb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          Change NDC &amp; Lot
          <Typography variant='subtitle1' sx={{ fontWeight: 700, mt: 1.5, lineHeight: 1.3 }}>
            {item.medicationName}
            {item.strength ? ` ${item.strength}` : ''}
          </Typography>
          <Typography variant='body2' color='text.secondary' component='div'>
            {rowDetails.join(' · ')}
          </Typography>
        </Box>
        <IconButton aria-label='Close' onClick={onClose} size='small' sx={{ mt: -0.5 }}>
          <CloseIcon fontSize='small' />
        </IconButton>
      </Box>

      {problem && (
        <Alert
          severity={problem.isShort ? 'warning' : 'error'}
          icon={
            problem.isShort ? (
              <WarningAmberIcon fontSize='small' />
            ) : (
              <ErrorOutlineIcon fontSize='small' />
            )
          }
          sx={{
            mt: 1.5,
            py: 0.25,
            typography: 'body2',
            // Amber from warningTone, not the theme's warning palette: that is the brand navy.
            ...(problem.isShort && {
              color: warningTextTone,
              bgcolor: (theme) => warningTint(theme, 0.12),
              border: '1px solid',
              borderColor: (theme) => warningTint(theme, 0.45),
              '& .MuiAlert-icon': { color: warningTone },
            }),
          }}
        >
          {problem.text}
        </Alert>
      )}
    </DialogTitle>
  );
};

ChangeNdcLotDialogTitle.propTypes = {
  item: PropTypes.shape({
    medicationName: PropTypes.string,
    strength: PropTypes.string,
    orderId: PropTypes.string,
    rxNumber: PropTypes.string,
    fulfillingPharmacyName: PropTypes.string,
    fillQuantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  fillQuantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  unusableRecordedLot: PropTypes.shape({
    lotNumber: PropTypes.string,
    remainingQty: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    expirationDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    existsInInventory: PropTypes.bool,
  }),
  /** POM-6536 — what the rest of the batch leaves on that lot for this row, when it shares it. */
  batchFreeQty: PropTypes.number,
  shortfallAllowed: PropTypes.bool,
  todayStr: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};

ChangeNdcLotDialogTitle.defaultProps = {
  fillQuantity: 0,
  unusableRecordedLot: null,
  batchFreeQty: null,
  shortfallAllowed: false,
};

export default ChangeNdcLotDialogTitle;
